/**
 * TimeOffHub.jsx
 * 
 * Employee portal hub for all time-off related functions.
 * Shows balances, pending requests, and links to request form.
 * 
 * Updated: Professional SVG icons matching Sage/BrightHR style
 * Auth fix: uses portalApi (employee_access_token) not axiosConfig
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';

// Portal-specific axios instance — reads employee_access_token, never access_token
const portalApi = axios.create({ baseURL: '/api' });
portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('employee_access_token');
      localStorage.removeItem('employee_refresh_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Professional SVG Icons - consistent 20x20, stroke-based
const Icons = {
  ArrowLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"></line>
      <polyline points="12,19 5,12 12,5"></polyline>
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Calendar: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  Clock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 16,14"></polyline>
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  ),
  X: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  ),
  Sun: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"></circle>
      <line x1="12" y1="1" x2="12" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="23"></line>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
      <line x1="1" y1="12" x2="3" y2="12"></line>
      <line x1="21" y1="12" x2="23" y2="12"></line>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
    </svg>
  ),
  Heart: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  ),
  Medical: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
      <line x1="12" y1="11" x2="12" y2="17"></line>
      <line x1="9" y1="14" x2="15" y2="14"></line>
    </svg>
  ),
  Users: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  Baby: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="5"></circle>
      <path d="M20 21a8 8 0 0 0-16 0"></path>
      <circle cx="10" cy="7" r="1" fill="currentColor"></circle>
      <circle cx="14" cy="7" r="1" fill="currentColor"></circle>
    </svg>
  ),
  Candle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="10" width="8" height="12" rx="1"></rect>
      <path d="M12 10V6"></path>
      <path d="M12 6c0-2 1-3 1-3s1 1 1 3c0 1-1 2-2 2s-2-1-2-2c0-2 1-3 1-3"></path>
    </svg>
  ),
  Compassion: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L8.5 5.5C7 7 7 9.5 8.5 11L12 14.5L15.5 11C17 9.5 17 7 15.5 5.5L12 2Z"></path>
      <path d="M4 15c0-1 1-2 2-2h2l2 2"></path>
      <path d="M20 15c0-1-1-2-2-2h-2l-2 2"></path>
      <path d="M2 19h4v3H2z"></path>
      <path d="M18 19h4v3h-4z"></path>
    </svg>
  ),
  Carer: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"></path>
      <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"></path>
      <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"></path>
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"></path>
    </svg>
  ),
  Scales: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21"></line>
      <path d="M5 8l7-4 7 4"></path>
      <path d="M5 8v0a3 3 0 0 0 3 3 3 3 0 0 0 3-3"></path>
      <path d="M13 8v0a3 3 0 0 0 3 3 3 3 0 0 0 3-3"></path>
      <line x1="8" y1="21" x2="16" y2="21"></line>
    </svg>
  ),
  Building: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="21" x2="21" y2="21"></line>
      <path d="M5 21V7l8-4v18"></path>
      <path d="M19 21V11l-6-4"></path>
      <line x1="9" y1="9" x2="9" y2="9.01"></line>
      <line x1="9" y1="13" x2="9" y2="13.01"></line>
      <line x1="9" y1="17" x2="9" y2="17.01"></line>
    </svg>
  ),
  Document: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14,2 14,8 20,8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10,9 9,9 8,9"></polyline>
    </svg>
  ),
};

// Shared styles matching EmployeePortal
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  headerGradient: {
    background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)',
    color: 'white',
    padding: '20px 16px',
    paddingTop: '24px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
    padding: '20px',
    marginBottom: '12px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  cardInteractive: {
    cursor: 'pointer',
  },
  buttonPrimary: {
    background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 24px',
    fontWeight: '600',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonSecondary: {
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 24px',
    fontWeight: '600',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  sectionHeader: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
    marginTop: '24px',
  },
  iconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};

const StatCard = ({ label, value, subtext }) => (
  <div style={{
    background: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    padding: '16px',
    flex: 1,
    minWidth: '100px',
  }}>
    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '24px', fontWeight: '700' }}>{value}</div>
    {subtext && <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>{subtext}</div>}
  </div>
);

const LEAVE_CATEGORIES = {
  annual_leave: { icon: Icons.Sun, label: 'Annual Leave', color: '#0d9488' },
  sick_leave: { icon: Icons.Heart, label: 'Sick Leave', color: '#ef4444' },
  medical_appointment: { icon: Icons.Medical, label: 'Medical Appointment', color: '#f59e0b' },
  maternity: { icon: Icons.Users, label: 'Maternity Leave', color: '#ec4899' },
  paternity: { icon: Icons.Users, label: 'Paternity Leave', color: '#8b5cf6' },
  adoption: { icon: Icons.Users, label: 'Adoption Leave', color: '#a855f7' },
  shared_parental: { icon: Icons.Users, label: 'Shared Parental Leave', color: '#6366f1' },
  neonatal_care: { icon: Icons.Baby, label: 'Neonatal Care Leave', color: '#14b8a6' },
  parental: { icon: Icons.Users, label: 'Parental Leave', color: '#0ea5e9' },
  bereavement: { icon: Icons.Candle, label: 'Bereavement Leave', color: '#64748b' },
  compassionate: { icon: Icons.Compassion, label: 'Compassionate Leave', color: '#eab308' },
  carers: { icon: Icons.Carer, label: "Carer's Leave", color: '#f97316' },
  dependant: { icon: Icons.Users, label: 'Dependant Leave', color: '#06b6d4' },
  jury_service: { icon: Icons.Scales, label: 'Jury Service', color: '#475569' },
  public_duties: { icon: Icons.Building, label: 'Public Duties', color: '#334155' },
  other: { icon: Icons.Document, label: 'Other', color: '#94a3b8' },
};

const getStatusStyle = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'approved': return { background: '#dcfce7', color: '#166534' };
    case 'pending':
    case 'submitted': return { background: '#fef3c7', color: '#92400e' };
    case 'rejected':
    case 'denied':
    case 'refused': return { background: '#fee2e2', color: '#991b1b' };
    case 'cancelled':
    case 'withdrawn': return { background: '#f1f5f9', color: '#475569' };
    default: return { background: '#f1f5f9', color: '#475569' };
  }
};

const TimeOffHub = ({ onBack, employeeData }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    holidayBalance: 0,
    totalDays: 28,
    takenDays: 0,
    pendingDays: 0,
    accruedDays: null,
    requests: [],
    neonatalEntitlement: null,
  });
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const dashboardResponse = await portalApi.get('/dashboard/user/dashboard');
      const dashData = dashboardResponse.data;

      let requests = [];
      try {
        const requestsResponse = await portalApi.get('/time-off/requests');
        requests = requestsResponse.data.requests || [];
      } catch (e) {
        requests = dashData.holidayRequests || [];
      }

      let neonatalEntitlement = null;
      try {
        const neonatalResponse = await portalApi.get('/time-off/neonatal/my-entitlements');
        if (neonatalResponse.data.entitlements?.length > 0) {
          neonatalEntitlement = neonatalResponse.data.entitlements[0];
        }
      } catch (e) { /* no neonatal entitlement */ }

      setData({
        holidayBalance: dashData.holidayBalance ?? 0,
        totalDays: dashData.totalDays ?? 28,
        takenDays: dashData.takenDays ?? 0,
        pendingDays: dashData.pendingDays ?? 0,
        accruedDays: dashData.accruedDays,
        requests,
        neonatalEntitlement,
      });
    } catch (err) {
      console.error('Failed to load time off data:', err);
    } finally {
      setLoading(false);
    }
  };

  const cancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    try {
      await portalApi.post(`/time-off/requests/${requestId}/cancel`);
      await loadData();
    } catch (err) {
      try {
        await portalApi.post(`/holidays/${requestId}/cancel`);
        await loadData();
      } catch (e) {
        alert('Failed to cancel request');
      }
    }
  };

  const filteredRequests = data.requests.filter(req => {
    if (filterType !== 'all' && req.leave_type !== filterType && req.reason !== filterType) return false;
    if (filterStatus !== 'all' && req.status?.toLowerCase() !== filterStatus) return false;
    return true;
  });

  const uniqueTypes = [...new Set(data.requests.map(r => r.leave_type || r.reason || 'annual_leave'))];

  const renderRequestCard = (request) => {
    const leaveType = request.leave_type || request.reason || 'annual_leave';
    const category = LEAVE_CATEGORIES[leaveType] || LEAVE_CATEGORIES.other;
    const IconComponent = category.icon;
    const startDate = request.start_date || request.startDate;
    const endDate = request.end_date || request.endDate;
    const days = request.number_of_days_holiday || request.days_requested || request.total_days || 1;
    const status = request.status || 'pending';
    const isPending = status.toLowerCase() === 'pending' || status.toLowerCase() === 'submitted';
    const isFuture = moment(startDate).isAfter(moment());

    return (
      <div key={request.id} style={{ ...styles.card, borderLeft: `4px solid ${category.color}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ ...styles.iconBox, background: `${category.color}15`, color: category.color }}>
              <IconComponent />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1e293b' }}>{category.label}</div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                {moment(startDate).format('D MMM')}
                {endDate && startDate !== endDate && ` - ${moment(endDate).format('D MMM YYYY')}`}
                {(!endDate || startDate === endDate) && ` ${moment(startDate).format('YYYY')}`}
              </div>
            </div>
          </div>
          <span style={{ ...styles.badge, ...getStatusStyle(status) }}>
            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {days} day{days !== 1 ? 's' : ''}
            {request.notes && <span style={{ marginLeft: '8px' }}>• {request.notes.substring(0, 30)}{request.notes.length > 30 ? '...' : ''}</span>}
          </div>
          {isPending && isFuture && (
            <button onClick={() => cancelRequest(request.id)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: '#dc2626', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
        {leaveType === 'neonatal_care' && request.neonatal_tier && (
          <div style={{ marginTop: '12px', padding: '8px 12px', background: '#f0fdfa', borderRadius: '8px', fontSize: '12px', color: '#0f766e' }}>
            Tier {request.neonatal_tier} • {request.neonatal_weeks} week{request.neonatal_weeks !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  };

  const renderOverview = () => (
    <>
      {data.accruedDays !== null && (
        <div style={{ ...styles.card, background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)', border: '1px solid #99f6e4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ ...styles.iconBox, background: '#0d9488', color: 'white' }}><Icons.Calendar /></div>
            <div>
              <div style={{ fontWeight: '600', color: '#134e4a' }}>Holiday Accrual</div>
              <div style={{ fontSize: '13px', color: '#0f766e' }}>{data.accruedDays.toFixed(1)} days earned so far this year</div>
            </div>
          </div>
          <div style={{ background: '#ccfbf1', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
            <div style={{ background: '#0d9488', height: '100%', width: `${Math.min(100, (data.accruedDays / data.totalDays) * 100)}%`, transition: 'width 0.5s ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#0f766e' }}>
            <span>Accrued: {data.accruedDays.toFixed(1)}</span>
            <span>Annual: {data.totalDays.toFixed(1)}</span>
          </div>
        </div>
      )}

      {data.neonatalEntitlement && data.neonatalEntitlement.weeks_remaining > 0 && (
        <div style={{ ...styles.card, background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)', border: '1px solid #e879f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ ...styles.iconBox, background: '#a855f7', color: 'white' }}><Icons.Baby /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', color: '#701a75' }}>Neonatal Care Leave</div>
              <div style={{ fontSize: '13px', color: '#86198f' }}>{data.neonatalEntitlement.weeks_remaining} weeks remaining</div>
            </div>
            <span style={{ ...styles.badge, background: '#f0abfc', color: '#701a75' }}>Active</span>
          </div>
          {data.neonatalEntitlement.deadline_date && (
            <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(168,85,247,0.1)', borderRadius: '8px', fontSize: '12px', color: '#7e22ce', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icons.Clock /> Must be taken by {moment(data.neonatalEntitlement.deadline_date).format('D MMM YYYY')}
            </div>
          )}
        </div>
      )}

      <div style={styles.sectionHeader}>Request Time Off</div>
      <div onClick={() => navigate('/employeeportal/time-off-request')} style={{ ...styles.card, ...styles.cardInteractive, display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.Plus /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>New Time Off Request</div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>Holiday, sick leave, family leave & more</div>
        </div>
        <div style={{ color: '#94a3b8' }}><Icons.ChevronRight /></div>
      </div>

      {data.requests.filter(r => r.status?.toLowerCase() === 'pending' || r.status?.toLowerCase() === 'submitted').length > 0 && (
        <>
          <div style={styles.sectionHeader}>Pending Requests</div>
          {data.requests.filter(r => r.status?.toLowerCase() === 'pending' || r.status?.toLowerCase() === 'submitted').slice(0, 3).map(req => renderRequestCard(req))}
          {data.requests.filter(r => r.status?.toLowerCase() === 'pending' || r.status?.toLowerCase() === 'submitted').length > 3 && (
            <button onClick={() => { setFilterStatus('pending'); setActiveTab('requests'); }} style={{ ...styles.buttonSecondary, width: '100%', marginTop: '8px' }}>
              View All Pending ({data.requests.filter(r => r.status?.toLowerCase() === 'pending' || r.status?.toLowerCase() === 'submitted').length})
            </button>
          )}
        </>
      )}

      {data.requests.filter(r => r.status?.toLowerCase() === 'approved' && moment(r.start_date || r.startDate).isAfter(moment())).length > 0 && (
        <>
          <div style={styles.sectionHeader}>Upcoming Time Off</div>
          {data.requests.filter(r => r.status?.toLowerCase() === 'approved' && moment(r.start_date || r.startDate).isAfter(moment())).sort((a, b) => moment(a.start_date || a.startDate).diff(moment(b.start_date || b.startDate))).slice(0, 3).map(req => renderRequestCard(req))}
        </>
      )}
    </>
  );

  const renderRequests = () => (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '8px' }}>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
          <option value="all">All Types</option>
          {uniqueTypes.map(type => <option key={type} value={type}>{LEAVE_CATEGORIES[type]?.label || type}</option>)}
        </select>
      </div>
      {filteredRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
          <div style={{ width: '64px', height: '64px', margin: '0 auto 16px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}><Icons.Calendar /></div>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>No Requests Found</div>
          <div style={{ fontSize: '14px' }}>{filterStatus !== 'all' || filterType !== 'all' ? 'Try adjusting your filters' : 'Your time off requests will appear here'}</div>
        </div>
      ) : (
        filteredRequests.sort((a, b) => moment(b.start_date || b.startDate).diff(moment(a.start_date || a.startDate))).map(req => renderRequestCard(req))
      )}
    </>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.headerGradient}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Time Off</h1>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#0d9488', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><Icons.ArrowLeft /></button>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>Time Off</h1>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Manage your leave</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', overflowX: 'auto', paddingBottom: '4px' }}>
          <StatCard label="Available" value={data.holidayBalance?.toFixed(1) || '0'} subtext="holiday days" />
          <StatCard label="Taken" value={data.takenDays?.toFixed(1) || '0'} subtext="days" />
          <StatCard label="Pending" value={data.pendingDays?.toFixed(1) || '0'} subtext="days" />
        </div>
      </div>

      <div style={{ display: 'flex', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        {['overview', 'requests'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: activeTab === tab ? '#0d9488' : '#64748b', borderBottom: activeTab === tab ? '2px solid #0d9488' : '2px solid transparent', transition: 'all 0.2s ease' }}>
            {tab === 'overview' && 'Overview'}{tab === 'requests' && `My Requests (${data.requests.length})`}
          </button>
        ))}
      </div>

      <div style={{ padding: '16px' }}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'requests' && renderRequests()}
      </div>
    </div>
  );
};

export default TimeOffHub;